import { createContext, useContext, useState, useEffect, ReactNode } from "react";

export type Language = "en" | "bg";

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType>({
  language: "en",
  setLanguage: (lang) => {
    if (typeof window !== "undefined") window.localStorage.setItem("lang", lang);
  },
  t: (key) => translationsEN[key] ?? key,
});

export const useLanguage = () => {
  return useContext(LanguageContext);
};

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [language, setLanguageState] = useState<Language>(() => {
    if (typeof window === "undefined") return "en";
    const stored = window.localStorage.getItem("lang");
    return stored === "bg" || stored === "en" ? stored : "en";
  });

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    if (typeof window !== "undefined") {
      window.localStorage.setItem("lang", lang);
    }
  };

  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.lang = language;
    }
  }, [language]);

  const t = (key: string): string => {
    const dict = language === "en" ? translationsEN : translationsBG;
    return dict[key] ?? translationsEN[key] ?? key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

const translationsEN: Record<string, string> = {
  // Navigation
  "nav.work": "My Work",
  "nav.about": "About Me",
  "nav.cv": "CV",
  "nav.portfolioReview": "Portfolio Review",
  "nav.contact": "Get in Touch",

  // Hero
  "hero.eyebrow": "LEAD PRODUCT DESIGNER & RESEARCHER",
  "hero.headline":
    "I'm Khaled, a multi-disciplinary designer who finds joy in creating purposeful products that are good for people using a touch of AI",
  "hero.sayHello": "Say Hello",
  "hero.currentlyAt": "Recent product work includes",
  "hero.previouslyAt": "and",
  "hero.basedIn": "Based in",
  "hero.sofia": "Sofia, Bulgaria",

  // Home – Projects section
  "home.projects.title": "My Work",
  "home.projects.subtitle": "Selected product, UX/UI, systems, and brand case studies",

  // Home – About section
  "home.about.title": "About Me",
  "home.about.subtitle": "Product, UX/UI & Design Systems",
  "home.about.p1":
    "I'm a multi-disciplinary designer with experience shaping digital products from product framing and UX architecture through interaction, interface craft, systems, and handoff.",
  "home.about.p2.before": "Recent work includes product design at",
  "home.about.p2.middle": " and design leadership at",
  "home.about.p2.after":
    ", spanning AI-assisted advertising and connected business tools.",
  "home.about.p3":
    "My approach connects product questions, user needs, information architecture, interaction, and visual systems. I use AI where it makes a workflow clearer or more useful—not as a substitute for product judgment.",
  "home.about.p4":
    "Based in Sofia, Bulgaria, I work with teams around the world to bridge the gap between complex technology and human needs, always keeping accessibility, ethics, and user empowerment at the forefront of my work.",
  "home.about.fullStory": "Full Story",
  "home.about.viewCV": "View CV",

  // Project card descriptions on Home
  "project.kasha.desc":
    "Send invoices, take bookings, get paid and manage clients. Sole trader, existing company or forming new - Kasha fits your setup.",
  "project.otis.desc":
    "An AI-assisted advertising product that helps businesses and agencies plan, create, and manage digital campaigns.",
  "project.alekbg.desc":
    "A shipped brand and multilingual commerce experience connecting retail discovery, product decisions and wholesale buying.",
  "project.opentag.desc":
    "A dual-sided loyalty product connecting player progression and rewards with operator configuration and control.",
  "project.wellmaxi.desc":
    "A bold identity and product catalogue helping professional buyers explore technical kitchen equipment.",
  "project.active.desc": "Real estate agency.",
  "project.letya.desc": "AI that helps your team work smarter — every day.",
  "project.banking.desc":
    "Reimagining mobile banking with AI-powered insights and seamless user experience for the modern consumer.",

  // CTA Banner
  "cta.tag": "Let's Collaborate",
  "cta.title": "Ready to craft something amazing?",
  "cta.subtitle":
    "Let's discuss how AI-powered design can transform your product. I'm available for freelance projects, consulting, and full-time opportunities.",
  "cta.button": "Get in Touch",
  "cta.years": "End-to-End Product Design",
  "cta.aiDesign": "UX, UI & Systems",

  // Footer
  "footer.tagline": "Product thinking, UX architecture, and interface craft",
  "footer.location": "📍 Sofia, Bulgaria · Available worldwide",
  "footer.available": "Available for work",
  "footer.navigation": "Navigation",
  "footer.workLink": "My Work",
  "footer.aboutLink": "About Me",
  "footer.cvLink": "CV & Experience",
  "footer.contactLink": "Contact",
  "footer.expertise": "Expertise",
  "footer.exp.uxui": "UX/UI Design",
  "footer.exp.ai": "AI Integration",
  "footer.exp.ds": "Design Systems",
  "footer.exp.research": "User Research",
  "footer.exp.leadership": "Design Leadership",
  "footer.connect": "Connect",
  "footer.email": "Email",
  "footer.linkedin": "LinkedIn",
  "footer.github": "GitHub",
  "footer.instagram": "Instagram",
  "footer.cta": "Get in Touch",
  "footer.copyright": "All rights reserved.",

  // About page
  "about.banner.title": "Letting you in.",
  "about.banner.subtitle": "The story behind the designer",
  "about.who.title": "Who is Khaled Kasmou?",
  "about.who.body":
    "Khaled Kasmou is a product and UX/UI designer based in Sofia, Bulgaria, working across product strategy, research framing, UX architecture, interface design, and design systems.",
  "about.background.title": "Background",
  "about.background.p1":
    "Ever since I can remember, I've always been curious about how people see and interact with the world around them. This curiosity led me to study Graphic Design and Fine Arts, exploring the intricacies of visual perception, cognition, and ultimately, human-computer interaction.",
  "about.background.p2":
    "My journey started in graphic design and expanded into UX, product design, and design leadership. Across roles at companies including Kasha.io and Otis AI, I have worked on connected business tools, AI-assisted products, and reusable design systems.",
  "about.what.title": "What I Do",
  "about.what.p1":
    "I specialize in creating intelligent, user-centered interfaces that balance automation with human control. Whether it's designing explainable AI systems, building scalable design systems, or conducting deep UX research, I focus on transforming complexity into clarity.",
  "about.what.p2":
    "My work spans B2B SaaS, e-commerce, fintech, and enterprise platforms, with experience collaborating across product, engineering, business, and design teams.",
  "about.what.li1": "UI Design — interface systems for web and mobile products",
  "about.what.li2": "UX Research — discovery, usability testing, and behavioral insights",
  "about.what.li3": "Design Systems — scalable Figma libraries and design tokens",
  "about.what.li4": "Design Leadership — mentoring and scaling product design teams",
  "about.what.li5": "AI-assisted Product Design — explainable, human-centered AI interfaces",
  "about.beyond.title": "Beyond Design",
  "about.beyond.body":
    "When I'm not designing, you'll find me exploring new AI tools, writing about design + AI ethics, or mentoring young designers. I speak Bulgarian, English, and Arabic, and I'm always eager to connect with people from different cultures and backgrounds.",
  "about.connect.title": "Let's Connect",
  "about.connect.email": "Email: khaledkasmou@gmail.com",
  "about.faq.title": "Frequently Asked Questions",
  "about.faq.q1": "Who is Khaled Kasmou?",
  "about.faq.a1":
    "Khaled Kasmou is a product and UX/UI designer based in Sofia, Bulgaria, with experience across startup, agency, and enterprise product teams.",
  "about.faq.q2": "What does Khaled Kasmou specialize in?",
  "about.faq.a2":
    "He specializes in product design, UX architecture, interface design, design systems, and research-informed product thinking.",
  "about.faq.q3": "What companies has Khaled worked with?",
  "about.faq.a3":
    "Khaled's recent work includes Otis AI and Kasha.io, alongside fintech, real-estate, and enterprise product experience.",
  "about.faq.q4": "Is Khaled available for freelance or consulting work?",
  "about.faq.a4":
    "Yes. Khaled takes on selected UX/UI design, design leadership, and design system consulting engagements for product teams and startups. Reach him via the Contact page.",

  // CV page
  "cv.banner.title": "Experience & Expertise",
  "cv.banner.subtitle": "Product design, UX/UI, systems, and design leadership",
  "cv.section.title": "Experience & Leadership",
  "cv.section.subtitle":
    "Experience across product strategy, UX/UI, systems, collaboration, and handoff",
  "cv.download": "Download CV",

  // Contact page
  "contact.title": "Let's work together",
  "contact.subtitle":
    "Have a project in mind or want to discuss AI-powered design? I'd love to hear from you.",
  "contact.form.name": "Name",
  "contact.form.namePlaceholder": "Your name",
  "contact.form.email": "Email",
  "contact.form.emailPlaceholder": "your.email@example.com",
  "contact.form.message": "Message",
  "contact.form.messagePlaceholder": "Tell me about your project or inquiry...",
  "contact.form.send": "Open in Email",
  "contact.form.sending": "Opening email...",
  "contact.toast.successTitle": "Message sent!",
  "contact.toast.successDesc": "Thank you for reaching out. I'll get back to you soon.",
  "contact.toast.errorTitle": "Error",
  "contact.toast.errorDesc": "Something went wrong. Please try again.",
  "contact.info.title": "Get in touch",
  "contact.info.emailLabel": "Email",
  "contact.help.title": "What I can help with",
  "contact.help.li1": "AI-powered product design and UX research",
  "contact.help.li2": "Design system development and optimization",
  "contact.help.li3": "User experience audits and improvements",
  "contact.help.li4": "Freelance projects and consulting",
  "contact.location":
    "Sofia, Bulgaria — working with clients worldwide. Available for remote collaboration and select on-site projects.",

  // Project Detail
  "pd.notFound": "Project not found",
  "pd.backHome": "Back to home",
  "pd.back": "Back",
  "pd.shipped": "Shipped",
  "pd.meta.product": "Product",
  "pd.meta.skills": "Skills",
  "pd.meta.role": "My Role",
  "pd.meta.timeline": "Timeline",
  "pd.section.background": "Background",
  "pd.section.challenge": "The Challenge",
  "pd.section.research": "Research",
  "pd.section.solution": "Solution",
  "pd.section.process": "Process",
  "pd.section.implementation": "Implementation",
  "pd.section.role": "My Role",
  "pd.section.impact": "Impact & Results",
  "pd.section.lessons": "Lessons Learned",
  "pd.reflection": "Reflection",
  "pd.carousel.title": "A true joint effort",
  "pd.carousel.subtitle":
    "The project required close collaboration with internal product teams and external partners, to ensure handoffs between different surfaces felt seamless, ensuring continuity and trust throughout the experience.",
  "pd.requestCS.title": "Request a case study",
  "pd.requestCS.body":
    "Want to learn more about this project? Get in touch to request a detailed case study.",
  "pd.requestCS.cta": "Get in Touch",
  "pd.next": "Next Project",

  // Proof strip & project cards
  "proof.title": "Core practice",
  "proof.stat1": "Product thinking and experience architecture",
  "proof.stat2": "Interface craft across responsive products",
  "proof.stat3": "Reusable patterns, systems, and handoff",
  "project.onRequest": "Case study on request",
  "home.about.downloadCV": "Download CV (PDF)",
};

const translationsBG: Record<string, string> = {
  // Navigation
  "nav.work": "Моята работа",
  "nav.about": "За мен",
  "nav.cv": "Автобиография",
  "nav.portfolioReview": "Преглед на портфолио",
  "nav.contact": "Свържи се",

  // Hero
  "hero.eyebrow": "ВОДЕЩ ПРОДУКТОВ ДИЗАЙНЕР И ИЗСЛЕДОВАТЕЛ",
  "hero.headline":
    "Аз съм Халед — мултидисциплинарен дизайнер, който намира радост в създаването на смислени продукти, добри за хората, с щипка AI",
  "hero.sayHello": "Кажи здрасти",
  "hero.currentlyAt": "Сред последните ми продукти са",
  "hero.previouslyAt": "и",
  "hero.basedIn": "Базиран в",
  "hero.sofia": "София, България",

  // Home – Projects section
  "home.projects.title": "Моята работа",
  "home.projects.subtitle":
    "Избрани казуси за продуктов дизайн, UX/UI, системи и бранд",

  // Home – About section
  "home.about.title": "За мен",
  "home.about.subtitle": "Продуктов дизайн, UX/UI и дизайн системи",
  "home.about.p1":
    "Аз съм мултидисциплинарен дизайнер с опит в развитието на дигитални продукти — от продуктова рамка и UX архитектура до интеракции, интерфейси, системи и предаване към разработка.",
  "home.about.p2.before": "Сред последните ми проекти е продуктова работа в",
  "home.about.p2.middle": " и дизайн лидерство в",
  "home.about.p2.after":
    ", с фокус върху AI-подпомогната реклама и свързани бизнес инструменти.",
  "home.about.p3":
    "Подходът ми съчетава задълбочено потребителско проучване с най-новите AI възможности. Вярвам, че добрият дизайн не е само естетика — той е разбиране на човешкото поведение, решаване на реални проблеми и създаване на преживявания, които изглеждат естествени, докато използват силата на изкуствения интелект.",
  "home.about.p4":
    "Базиран в София, България, работя с екипи от целия свят, за да преодолявам разстоянието между сложните технологии и човешките нужди, винаги с фокус върху достъпността, етиката и овластяването на потребителя.",
  "home.about.fullStory": "Цялата история",
  "home.about.viewCV": "Виж CV",

  // Project card descriptions on Home
  "project.kasha.desc":
    "Изпращай фактури, приемай резервации, получавай плащания и управлявай клиенти. Самонаето лице, съществуваща компания или нова — Kasha се вписва в твоята настройка.",
  "project.otis.desc":
    "AI-подпомогнат рекламен продукт за планиране, създаване и управление на дигитални кампании.",
  "project.alekbg.desc":
    "Реализиран бранд и многоезично commerce изживяване, свързващо retail откриването, продуктовите решения и wholesale покупките.",
  "project.opentag.desc":
    "Двустранен loyalty продукт, свързващ player прогреса и наградите с operator конфигурацията и контрола.",
  "project.wellmaxi.desc":
    "Смела идентичност и продуктов каталог за професионални купувачи на техническо кухненско оборудване.",
  "project.active.desc": "Агенция за недвижими имоти.",
  "project.letya.desc": "AI, който помага на екипа ти да работи по-умно — всеки ден.",
  "project.banking.desc":
    "Преосмисляне на мобилното банкиране с AI-задвижвани прозрения и безпроблемно потребителско изживяване за модерния клиент.",

  // CTA Banner
  "cta.tag": "Нека работим заедно",
  "cta.title": "Готов ли си да създадем нещо страхотно?",
  "cta.subtitle":
    "Нека обсъдим как AI-задвижваният дизайн може да трансформира твоя продукт. На разположение съм за фрийланс проекти, консултации и пълно работно време.",
  "cta.button": "Свържи се",
  "cta.years": "Цялостен продуктов дизайн",
  "cta.aiDesign": "UX, UI и системи",

  // Footer
  "footer.tagline": "Продуктово мислене, UX архитектура и интерфейсен дизайн",
  "footer.location": "📍 София, България · Работя по целия свят",
  "footer.available": "Свободен за работа",
  "footer.navigation": "Навигация",
  "footer.workLink": "Моята работа",
  "footer.aboutLink": "За мен",
  "footer.cvLink": "CV и опит",
  "footer.contactLink": "Контакт",
  "footer.expertise": "Експертиза",
  "footer.exp.uxui": "UX/UI Дизайн",
  "footer.exp.ai": "AI интеграция",
  "footer.exp.ds": "Дизайн системи",
  "footer.exp.research": "Потребителско проучване",
  "footer.exp.leadership": "Дизайн лидерство",
  "footer.connect": "Свържи се",
  "footer.email": "Имейл",
  "footer.linkedin": "LinkedIn",
  "footer.github": "GitHub",
  "footer.instagram": "Instagram",
  "footer.cta": "Свържи се",
  "footer.copyright": "Всички права запазени.",

  // About page
  "about.banner.title": "Малко за мен.",
  "about.banner.subtitle": "Историята зад дизайнера",
  "about.who.title": "Кой е Халед Касму?",
  "about.who.body":
    "Халед Касму е продуктов и UX/UI дизайнер, базиран в София, България, с опит в продуктова стратегия, рамкиране на проучвания, UX архитектура, интерфейсен дизайн и дизайн системи.",
  "about.background.title": "Произход",
  "about.background.p1":
    "Откакто се помня, винаги съм бил любопитен как хората възприемат и взаимодействат със света около тях. Това любопитство ме отведе да изучавам Графичен дизайн и Изящни изкуства, изследвайки тънкостите на визуалното възприятие, познанието и в крайна сметка взаимодействието човек–компютър.",
  "about.background.p2":
    "Пътят ми започна в графичния дизайн и продължи към UX, продуктов дизайн и дизайн лидерство. В компании като Kasha.io и Otis AI работих по свързани бизнес инструменти, AI-подпомогнати продукти и преизползваеми дизайн системи.",
  "about.what.title": "С какво се занимавам",
  "about.what.p1":
    "Специализирам в създаването на интелигентни, ориентирани към потребителя интерфейси, които балансират автоматизацията с човешкия контрол. Дали ще е проектиране на обясними AI системи, изграждане на скалируеми дизайн системи или провеждане на дълбоко UX проучване — фокусът ми е да превръщам сложността в яснота.",
  "about.what.p2":
    "Работата ми обхваща B2B SaaS, електронна търговия, финтех и корпоративни платформи, в сътрудничество с продуктови, инженерни, бизнес и дизайн екипи.",
  "about.what.li1": "UI дизайн — интерфейсни системи за уеб и мобилни продукти",
  "about.what.li2": "UX проучване — открития, тестове за използваемост и поведенчески прозрения",
  "about.what.li3": "Дизайн системи — скалируеми Figma библиотеки и design tokens",
  "about.what.li4": "Дизайн лидерство — менторство и развитие на продуктови дизайн екипи",
  "about.what.li5": "AI-асистиран продуктов дизайн — обясними, ориентирани към човека AI интерфейси",
  "about.beyond.title": "Извън дизайна",
  "about.beyond.body":
    "Когато не проектирам, ще ме намериш да изследвам нови AI инструменти, да пиша за дизайн + AI етика или да менторирам млади дизайнери. Говоря български, английски и арабски и винаги съм отворен да се свързвам с хора от различни култури и среди.",
  "about.connect.title": "Нека се свържем",
  "about.connect.email": "Имейл: khaledkasmou@gmail.com",
  "about.faq.title": "Често задавани въпроси",
  "about.faq.q1": "Кой е Халед Касму?",
  "about.faq.a1":
    "Халед Касму е продуктов и UX/UI дизайнер, базиран в София, България, с опит в стартъп, агенционни и корпоративни продуктови екипи.",
  "about.faq.q2": "В какво специализира Халед Касму?",
  "about.faq.a2":
    "Той специализира в продуктов дизайн, UX архитектура, интерфейсен дизайн, дизайн системи и продуктово мислене, подкрепено от проучвания.",
  "about.faq.q3": "С кои компании е работил Халед?",
  "about.faq.a3":
    "Сред последните проекти на Халед са Otis AI и Kasha.io, наред с работа по финтех, недвижими имоти и корпоративни продукти.",
  "about.faq.q4": "Свободен ли е Халед за фрийланс или консултации?",
  "about.faq.a4":
    "Да. Халед поема избрани UX/UI дизайн, дизайн лидерство и консултации по дизайн системи за продуктови екипи и стартъпи. Свържи се чрез страницата за контакти.",

  // CV page
  "cv.banner.title": "Опит и експертиза",
  "cv.banner.subtitle":
    "Продуктов дизайн, UX/UI, системи и дизайн лидерство",
  "cv.section.title": "Опит и лидерство",
  "cv.section.subtitle":
    "Опит в продуктова стратегия, UX/UI, системи, сътрудничество и предаване",
  "cv.download": "Изтегли CV",

  // Contact page
  "contact.title": "Нека работим заедно",
  "contact.subtitle":
    "Имаш проект наум или искаш да обсъдим дизайн с AI? С удоволствие ще те изслушам.",
  "contact.form.name": "Име",
  "contact.form.namePlaceholder": "Твоето име",
  "contact.form.email": "Имейл",
  "contact.form.emailPlaceholder": "your.email@example.com",
  "contact.form.message": "Съобщение",
  "contact.form.messagePlaceholder": "Разкажи ми за проекта или запитването си...",
  "contact.form.send": "Отвори в имейл",
  "contact.form.sending": "Отваряне на имейл...",
  "contact.toast.successTitle": "Съобщението е изпратено!",
  "contact.toast.successDesc": "Благодаря, че се свърза. Ще ти отговоря скоро.",
  "contact.toast.errorTitle": "Грешка",
  "contact.toast.errorDesc": "Нещо се обърка. Моля, опитай отново.",
  "contact.info.title": "Свържи се",
  "contact.info.emailLabel": "Имейл",
  "contact.help.title": "С какво мога да помогна",
  "contact.help.li1": "Продуктов дизайн с AI и UX проучване",
  "contact.help.li2": "Разработване и оптимизация на дизайн системи",
  "contact.help.li3": "Одити и подобрения на потребителското изживяване",
  "contact.help.li4": "Фрийланс проекти и консултации",
  "contact.location":
    "София, България — работя с клиенти по целия свят. На разположение за дистанционно сътрудничество и избрани проекти на място.",

  // Project Detail
  "pd.notFound": "Проектът не е намерен",
  "pd.backHome": "Обратно към началото",
  "pd.back": "Назад",
  "pd.shipped": "Пуснат",
  "pd.meta.product": "Продукт",
  "pd.meta.skills": "Умения",
  "pd.meta.role": "Моята роля",
  "pd.meta.timeline": "Период",
  "pd.section.background": "Контекст",
  "pd.section.challenge": "Предизвикателството",
  "pd.section.research": "Проучване",
  "pd.section.solution": "Решение",
  "pd.section.process": "Процес",
  "pd.section.implementation": "Реализация",
  "pd.section.role": "Моята роля",
  "pd.section.impact": "Въздействие и резултати",
  "pd.section.lessons": "Научени уроци",
  "pd.reflection": "Размисъл",
  "pd.carousel.title": "Истинско съвместно усилие",
  "pd.carousel.subtitle":
    "Проектът изискваше тясно сътрудничество с вътрешни продуктови екипи и външни партньори, за да се гарантира, че преходите между различните повърхности са безпроблемни и поддържат непрекъснатост и доверие в цялото изживяване.",
  "pd.requestCS.title": "Поискай казус",
  "pd.requestCS.body":
    "Искаш ли да научиш повече за този проект? Свържи се, за да поискаш подробен казус.",
  "pd.requestCS.cta": "Свържи се",
  "pd.next": "Следващ проект",

  // Proof strip & project cards
  "proof.title": "Основна практика",
  "proof.stat1": "Продуктово мислене и архитектура на изживяването",
  "proof.stat2": "Интерфейсен дизайн за адаптивни продукти",
  "proof.stat3": "Преизползваеми модели, системи и предаване",
  "project.onRequest": "Казус при запитване",
  "home.about.downloadCV": "Изтегли CV (PDF)",
};
